import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface MarketingChannel {
  id: string;
  name: string;
  icon: string;
  color: string;
  visitors: number;
  leads: number;
  cost: number;
  roi: string;
  trend: string;
  display_order: number;
}

export interface MarketingCampaign {
  id: string;
  name: string;
  channel_id: string | null;
  channel_name: string;
  status: string;
  budget: number;
  spent: number;
  reach: number;
  conversions: number;
  start_date: string | null;
  end_date: string | null;
  progress: number;
}

export function useMarketingData() {
  const [channels, setChannels] = useState<MarketingChannel[]>([]);
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, caRes] = await Promise.all([
        fetch(`${API_URL}/api/marketing/channels`, { credentials: "include" }),
        fetch(`${API_URL}/api/marketing/campaigns`, { credentials: "include" }),
      ]);
      if (chRes.ok) setChannels((await chRes.json()).channels || []);
      if (caRes.ok) setCampaigns((await caRes.json()).campaigns || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createCampaign = async (campaign: Partial<MarketingCampaign>) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/campaigns`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(campaign),
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(prev => [data, ...prev]);
        toast.success("Campagne créée !");
        return data;
      }
    } catch { toast.error("Erreur"); }
    return null;
  };

  const deleteCampaign = async (id: string) => {
    await fetch(`${API_URL}/api/marketing/campaigns/${id}`, { method: "DELETE", credentials: "include" });
    setCampaigns(prev => prev.filter(c => c.id !== id));
    toast.success("Campagne supprimée");
  };

  const updateChannel = async (id: string, updates: Partial<MarketingChannel>) => {
    await fetch(`${API_URL}/api/marketing/channels/${id}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    setChannels(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  return { channels, campaigns, loading, createCampaign, deleteCampaign, updateChannel, refresh: fetchAll };
}
